// DESIGN-SPEC §5.2 Button 按钮
"use client";

import Link from "next/link";
import type {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
} from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant =
  | "buy"
  | "brand"
  | "outline"
  | "outline-inverse"
  | "fill-inverse"
  | "text";

export interface ButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children" | "className" | "onClick"
  > {
  children: ReactNode;
  variant: ButtonVariant;
  size?: "default" | "sm";
  href?: string;
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
}

const variantClasses: Record<ButtonVariant, string> = {
  buy: "bg-cinnabar text-white shadow-[0_7px_18px_rgba(184,67,44,0.2)] active:bg-cinnabar-press",
  brand: "bg-forest-700 text-paper-inverse shadow-[0_7px_18px_rgba(16,40,27,0.18)] active:brightness-90",
  outline:
    "border border-forest-500 bg-transparent text-forest-700 active:brightness-90",
  "outline-inverse":
    "border border-paper-inverse bg-transparent text-paper-inverse active:brightness-90",
  "fill-inverse":
    "bg-paper-inverse text-forest-900 active:brightness-90",
  text: "h-auto bg-transparent px-0 text-sm font-normal text-forest-700 underline underline-offset-4 active:brightness-90",
};

/**
 * `buy` 变体只允许用于立即购买、提交订单、立即兑换等金钱动作。
 * 品牌、导航或装饰动作必须使用其他变体。
 */
export function Button({
  children,
  variant,
  size = "default",
  href,
  className,
  disabled,
  onClick,
  type = "button",
  ...props
}: ButtonProps) {
  const classes = cn(
    "inline-flex shrink-0 items-center justify-center gap-1.5 font-body font-semibold no-underline transition-[transform,background-color,filter] duration-[120ms] active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-500",
    variant === "text"
      ? undefined
      : size === "sm"
        ? "min-h-11 rounded-[10px] px-3.5 text-sm"
        : "h-12 rounded-[12px] px-5 text-base",
    variantClasses[variant],
    disabled &&
      "pointer-events-none border-line bg-line text-ink-3 no-underline opacity-100 disabled:border-line disabled:bg-line disabled:text-ink-3",
    className,
  );

  if (href && !disabled) {
    return (
      <Link
        href={href}
        className={classes}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement> | undefined}
        aria-disabled={disabled || undefined}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      {...props}
      type={type}
      disabled={disabled}
      className={classes}
      onClick={onClick as MouseEventHandler<HTMLButtonElement> | undefined}
    >
      {children}
    </button>
  );
}
